/**
 * Messaging > Message builder: the approved full builder, bound to the real
 * message-template and shared-layout APIs.
 *
 * Shared layouts and message templates in one workspace (rail + editor). All
 * data comes from the typed api-client; unsaved edits are in memory only and
 * are protected on leave. Groups for modules this tenant does not have
 * (appointments, queues on an events-only tenant) are not shown. Writes need
 * `messaging.templates.write`; without it everything is read only.
 *
 * Deep links (`messaging.builder` params): `part` ("messages" | "layouts"),
 * `messageId`, `channel` ("email" | "sms"), `layoutKey`, `templateId`. A Journey `messageId` names one
 * channel (`asset_event_confirmation:sms`); it opens the shared message on that channel's tab.
 * A linked target is kept while the lists load and is never replaced by another message, channel or
 * layout: if it can't be found, the screen says so and the author chooses (see `resolveMessageSelection`).
 *
 * Data: bound (customizeMessageTemplate and the layout read-only refusal observed 2026-10-13; see changelog).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Banner, Btn, Drawer, Icon, IconButton, PageState, type PreviewDevice } from "@v0y4ge/jrni-ui";
import { useT } from "../../../../lib/i18n";
import { moduleKnownAbsent, useTenantModules } from "../../../../lib/tenant-modules";
import { can } from "../../../../shell/access";
import { useNav } from "../../../../shell/router";
import { useIsMobile } from "../../../../shell/useResponsive";
import { MessageDraftBoundary, useDraftLeaveGuard } from "../draft-navigation";
import type { Failure } from "./api";
import { BUILDER_CSS } from "./builder-css";
import { useLayoutLibrary, useTemplateCatalogue } from "./data";
import {
  catalogueLanguageViews, channelName, channelOfMessageId, DEFAULT_LANGUAGE_VIEW, groupCatalogue, GROUP_MODULE, messagePresentationKey, resolveLayoutSelection, resolveMessageSelection,
  type CatalogueMessage, type Channel, type MessageTarget, type Tr,
} from "./model";
import { ensureSourceDeps } from "./source-deps";
import { BODY_BUILDER_ID, gateBuilder } from "./useBodyBuilder";
import { LAYOUT_BUILDER_ID } from "./useLayoutBuilder";
import { useLayoutStore } from "./useLayouts";
import { useTemplateStore } from "./useTemplates";
import { CONTROL_H, EditorSkeleton, FailureBanner, FooterSkeleton } from "./views/common";
import { LayoutEditor } from "./views/LayoutEditor";
import { MessageRail, type Area } from "./views/MessageRail";
import { TemplateEditor } from "./views/TemplateEditor";

ensureSourceDeps();

/** The row scope while the catalogue holds no rows. */
const NO_ROWS_SCOPE: (string | null)[] = [null];
const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
/** The builder-css breakpoint below which the rail is a Drawer (tablet and phone) instead of an inline column. */
const RAIL_DRAWER_BP = 1024;
/** The builder-css phone breakpoint: the list trigger is icon only, and the preview opens on Mobile. */
const PHONE_BP = 640;
const isPhoneNow = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(`(max-width:${PHONE_BP}px)`).matches;

export function MessageBuilderPage() {
  const t = useT() as Tr;
  return (
    <div className="msgb-page" data-testid="msgb-page">
      <style>{BUILDER_CSS}</style>
      <h1 className="msgb-sr">{t("messaging.builder.title", "Message builder")}</h1>
      <MessageDraftBoundary t={t}>
        <BuilderWorkspace t={t} />
      </MessageDraftBoundary>
    </div>
  );
}

/** A linked target that can't be shown: says so, names the link's reference, offers recovery. Opens nothing else. */
function Unavailable({ t, title, text, reference, actions, testid, railTrigger }: { t: Tr; title: string; text: string; reference: string; actions: ReactNode; testid: string; railTrigger: ReactNode }) {
  return (
    <div className="msgb-split" data-testid={testid}>
      {railTrigger ? <div className="msgb-edhead">{railTrigger}</div> : null}
      <div className="msgb-center">
        <div style={{ maxWidth: "min(560px, 100%)", width: "100%" }}>
          <Banner tone="warning" size="compact" title={title}>
            <span className="msgb-stack">
              <span>{text}</span>
              <span className="msgb-small">{t("messaging.builder.unavailable.reference", "Link reference")}: <code>{reference}</code></span>
              <span className="msgb-actions">{actions}</span>
            </span>
          </Banner>
        </div>
      </div>
    </div>
  );
}

function BuilderWorkspace({ t }: { t: Tr }) {
  const nav = useNav();
  const params = nav.params || {};
  const canWrite = can("messaging.templates.write");
  const mods = useTenantModules();

  const catalogue = useTemplateCatalogue();
  const library = useLayoutLibrary();
  const templates = useTemplateStore();
  const layouts = useLayoutStore(library.reload);

  const [area, setArea] = useState<Area>(str(params.part) === "layouts" || str(params.layoutKey) ? "layouts" : "messages");
  // What the author asked for. A link target is EXPLICIT: it is kept while the catalogue loads and, if it can't be
  // found once the catalogue has loaded, the screen says so; it is never swapped for another message or channel.
  const [target, setTarget] = useState<MessageTarget>(() => {
    const linkedMessage = str(params.messageId), linkedTemplate = str(params.templateId), linkedChannel = str(params.channel);
    const ch: Channel | null = linkedChannel === "sms" || linkedChannel === "email" ? linkedChannel : linkedMessage ? channelOfMessageId(linkedMessage) : null;
    return { key: linkedMessage ? messagePresentationKey(linkedMessage) : null, templateId: linkedTemplate, channel: ch ?? "email",
      explicit: { message: !!(linkedMessage || linkedTemplate), channel: !!ch } };
  });
  const [layoutTarget, setLayoutTarget] = useState<{ key: string | null; explicit: boolean }>(() => ({ key: str(params.layoutKey), explicit: !!str(params.layoutKey) }));
  const created = useRef(new Set<string>());
  const [locale, setLocale] = useState<string>(DEFAULT_LANGUAGE_VIEW);
  // A phone opens the preview on Mobile (a 600px+ desktop email fitted into a phone is too small to read); the author's
  // own choice is kept from then on.
  const [device, setDevice] = useState<PreviewDevice>(() => (isPhoneNow() ? "mobile" : "desktop"));
  // On a tablet or phone the list is a Drawer, closed at first so the editor is what shows; "Show list" opens it.
  const compact = useIsMobile(RAIL_DRAWER_BP);
  const phone = useIsMobile(PHONE_BP);
  const [railOpen, setRailOpen] = useState(false);
  useEffect(() => { if (compact) setRailOpen(false); }, [compact]);
  const [createFailure, setCreateFailure] = useState<Failure | null>(null);
  const [creating, setCreating] = useState(false);

  // Leaving the screen with unsaved edits (or a write in flight) asks first.
  useDraftLeaveGuard(templates.anyDirty() || layouts.anyDirty(), false, t);

  const rows = catalogue.data || [];
  const hiddenGroup = (groupId: string) => { const m = GROUP_MODULE[groupId]; return m ? moduleKnownAbsent(m, mods) : false; };
  // The tenant's own language opens first: the chosen Language choice, else the first one the catalogue holds.
  const views = useMemo(() => catalogueLanguageViews(rows), [rows]);
  const view = views.find((v) => v.value === locale) ?? views[0] ?? null;
  const scope = view ? view.locales : NO_ROWS_SCOPE;
  const groups = useMemo(() => groupCatalogue(rows, scope, hiddenGroup), [rows, scope, mods]); // eslint-disable-line react-hooks/exhaustive-deps
  const locales = useMemo(() => views.map((v) => ({ value: v.value, label: v.locale ?? t("messaging.builder.defaultLanguage", "Default language") })), [views, t]);

  // What the editor shows is RESOLVED from the target; nothing here ever picks "the first one" for an explicit target.
  const resolved = resolveMessageSelection({ phase: catalogue.phase, rows, locale: scope, hiddenGroup, target });
  // The linked message exists in another language: show that language (it is the same target, not a substitute).
  const relocaleTo = resolved.kind === "relocale" ? resolved.locale : undefined;
  const relocale = relocaleTo === undefined ? null : (views.find((v) => v.locales.includes(relocaleTo))?.value ?? null);
  useEffect(() => { if (relocale !== null) setLocale(relocale); }, [relocale]);
  const current: CatalogueMessage | null = resolved.kind === "selected" ? resolved.message : null;
  const channel: Channel = resolved.kind === "selected" ? resolved.channel : target.channel;
  const lib = library.data;
  const layoutResolved = resolveLayoutSelection({ phase: library.phase, layouts: lib ? lib.layouts : [], defaultKey: lib ? lib.defaultLayout.layoutKey : null,
    target: layoutTarget, known: created.current });
  const layoutKey = layoutResolved.kind === "selected" ? layoutResolved.layoutKey : null;

  /** Moving the selection first commits a pending inspector edit; a refused one keeps the selection. */
  const leaveEditor = (): boolean => gateBuilder(BODY_BUILDER_ID).ok && gateBuilder(LAYOUT_BUILDER_ID).ok;
  const closeRailOnPhone = () => { if (compact) setRailOpen(false); };
  // "Choose a message": move focus to a row of the list. `key`: the message a link named, when it is in the list
  // (a missing channel); its row takes focus, else the selected row, else the first.
  const focusListRow = (key?: string) => {
    const rows = "#msgb-railbody [data-jrni-component=\"media-row\"]";
    const named = key ? document.querySelector<HTMLElement>(`#msgb-railbody [data-testid="msgb-row-${CSS.escape(key)}"]`) : null;
    const row = named || document.querySelector<HTMLElement>(`${rows}[aria-selected="true"]`) || document.querySelector<HTMLElement>(rows);
    if (row) row.focus();
  };
  // On a tablet or phone the list opens in a Drawer, whose focus trap focuses its first control (the close button)
  // in its own effect. This effect runs after the Drawer's, so the row the author asked for keeps focus; Tab still
  // stays inside the Drawer.
  const pendingRowFocus = useRef<{ key?: string } | null>(null);
  useEffect(() => {
    if (!railOpen || !pendingRowFocus.current) return;
    const want = pendingRowFocus.current; pendingRowFocus.current = null;
    focusListRow(want.key);
  }, [railOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const revealList = (key?: string) => {
    if (compact) { pendingRowFocus.current = { key }; setRailOpen(true); return; }
    focusListRow(key);
  };

  // Deliberate choices: each one becomes the new (explicit) target, so the author always gets the item THEY chose.
  const openLayout = (key: string) => { if (!leaveEditor()) return; setArea("layouts"); setLayoutTarget({ key, explicit: true }); closeRailOnPhone(); };
  const openMessage = (m: CatalogueMessage) => {
    if (!leaveEditor()) return;
    setArea("messages");
    setTarget((t0) => ({ key: m.key, templateId: null, channel: t0.channel, explicit: { message: true, channel: false } }));
    closeRailOnPhone();
  };
  const chooseChannel = (key: string, ch: Channel) => setTarget({ key, templateId: null, channel: ch, explicit: { message: true, channel: true } });
  const openTemplate = (templateId: string) => { setArea("messages"); setTarget((t0) => ({ key: null, templateId, channel: t0.channel, explicit: { message: true, channel: true } })); };
  const changeLocale = (v: string) => {
    if (!leaveEditor()) return;
    setLocale(v);
    // A deliberate language switch keeps the message when that language has it, else opens the first one.
    setTarget((t0) => ({ key: current ? current.key : t0.key, templateId: null, channel: channel, explicit: { message: false, channel: false } }));
  };
  const newLayout = async (from: "default" | "blank") => {
    if (!leaveEditor() || !lib) return;
    setCreateFailure(null); setCreating(true);
    const name = from === "default" && defaultRow
      ? `${defaultRow.name} ${t("messaging.builder.copySuffix", "(copy)")}`.slice(0, 160)
      : t("messaging.builder.newLayout.name", "New layout");
    const r = await layouts.create(name, from === "default" && lib.defaultLayout.layoutKey
      ? { kind: "copy", layoutKey: lib.defaultLayout.layoutKey, version: "published" }
      : { kind: "blank" });
    setCreating(false);
    if (r.ok) { created.current.add(r.value.layoutKey); setArea("layouts"); setLayoutTarget({ key: r.value.layoutKey, explicit: true }); closeRailOnPhone(); } else setCreateFailure(r.failure);
  };

  const defaultRow = lib && lib.defaultLayout.layoutKey ? lib.layouts.find((l) => l.layoutKey === lib.defaultLayout.layoutKey) : null;
  const defaultPublished = !!(defaultRow && defaultRow.published && !defaultRow.archived);
  const messageDirty = (m: CatalogueMessage) => !!((m.rows.email && templates.dirty(m.rows.email.templateId)) || (m.rows.sms && templates.dirty(m.rows.sms.templateId)));

  const rail = (
    <MessageRail t={t} area={area} onArea={(a) => { if (leaveEditor()) setArea(a); }} canWrite={canWrite}
      catalogue={{ phase: catalogue.phase, groups, failure: catalogue.failure, refreshing: catalogue.phase === "ready" && catalogue.refreshing, reload: catalogue.reload }}
      locales={locales} locale={view ? view.value : DEFAULT_LANGUAGE_VIEW} onLocale={changeLocale}
      selectedMessageKey={current ? current.key : null} onMessage={openMessage} messageDirty={messageDirty}
      layouts={{ phase: library.phase, rows: lib ? lib.layouts : [], failure: library.failure, refreshing: library.phase === "ready" && library.refreshing, reload: library.reload }}
      selectedLayoutKey={layoutKey} onLayout={openLayout} layoutDirty={(k) => layouts.dirty(k)}
      onNewLayout={(f) => void newLayout(f)} newLayoutBusy={creating} defaultPublished={defaultPublished} defaultName={defaultRow ? defaultRow.name : null} />
  );
  // Tablet and phone: the list opens in a left Drawer from the editor header's "Show list".
  // On a phone it is icon only, so the title keeps the header's width.
  const railTrigger = compact ? (
    <span className="msgb-railtrigger">
      {phone ? (
        <IconButton size={CONTROL_H} label={t("messaging.builder.rail.show", "Show list")} aria-expanded={railOpen} onClick={() => setRailOpen(true)} testid="msgb-rail-show">
          <Icon.list size={16} />
        </IconButton>
      ) : (
        <Btn variant="secondary" size="md" icon={<Icon.list size={14} />} aria-expanded={railOpen} onClick={() => setRailOpen(true)} testid="msgb-rail-show">
          {t("messaging.builder.rail.show", "Show list")}
        </Btn>
      )}
    </span>
  ) : null;
  /** A state that fills the editor column (failed, empty), centred, with the list trigger above it. */
  const centred = (body: ReactNode, testid?: string) => (
    <div className="msgb-split" data-testid={testid}>
      {railTrigger ? <div className="msgb-edhead">{railTrigger}</div> : null}
      <div className="msgb-center"><div style={{ maxWidth: "min(560px, 100%)", width: "100%" }}>{body}</div></div>
    </div>
  );

  return (
    <div className="msgb-grid">
      {compact ? (
        <Drawer open={railOpen} side="left" width={300} onClose={() => setRailOpen(false)} title={t("messaging.builder.rail.areas", "Template areas")}>
          {rail}
        </Drawer>
      ) : rail}
      <main className="msgb-main" id="msgb-main" aria-label={area === "layouts" ? t("messaging.builder.area.layouts", "Shared email layouts") : t("messaging.builder.area.messages", "Message templates")}>
        {createFailure ? <div className="msgb-edbody"><FailureBanner t={t} failure={createFailure} onDismiss={() => setCreateFailure(null)} /></div> : null}
        {area === "messages" ? (
          resolved.kind === "selected" && current ? (
            <TemplateEditor t={t} store={templates} message={current} channel={channel}
              onChannel={(ch) => chooseChannel(current.key, ch)} library={lib} canWrite={canWrite} onOpenLayout={openLayout}
              device={device} onDevice={setDevice} onRowChanged={catalogue.reload} railTrigger={railTrigger} />
          ) : resolved.kind === "unavailable" ? (
            <Unavailable t={t} testid={`msgb-unavailable-${resolved.what}`} railTrigger={railTrigger}
              title={resolved.what === "channel"
                ? `${t("messaging.builder.unavailable.channelTitle", "This message has no version for")} ${channelName(resolved.channel, t)}`
                : t("messaging.builder.unavailable.messageTitle", "The linked message isn't available")}
              text={resolved.what === "channel"
                ? t("messaging.builder.unavailable.channelText", "The link asks for a channel this message doesn't have. Nothing else has been opened in its place.")
                : t("messaging.builder.unavailable.messageText", "It isn't in this list: it may have been removed, belong to a feature this workspace doesn't use, or be edited elsewhere. Nothing else has been opened in its place; choose a message from the list.")}
              reference={resolved.ref}
              actions={<>
                {resolved.what === "channel" && resolved.available ? (() => { const other = resolved.available; const key = resolved.message.key; return (
                  <Btn size="sm" variant="secondary" onClick={() => { if (leaveEditor()) chooseChannel(key, other); }} testid="msgb-unavailable-open-other">
                    {`${t("messaging.builder.unavailable.openOther", "Open the version for")} ${channelName(other, t)}`}
                  </Btn>); })() : null}
                {resolved.what !== "channel" ? <Btn size="sm" variant="secondary" onClick={catalogue.reload}>{t("messaging.builder.unavailable.reload", "Reload the list")}</Btn> : null}
                <Btn size="sm" variant="ghost" onClick={() => revealList(resolved.what === "channel" ? resolved.message.key : undefined)}>{t("messaging.builder.unavailable.choose", "Choose a message")}</Btn>
              </>} />
          ) : resolved.kind === "failed" ? (
            // A refused list read is always shown here with Try again (on a tablet or phone the list is in a closed
            // Drawer); a linked target also says it opens once the list loads.
            catalogue.failure ? centred(<FailureBanner t={t} failure={catalogue.failure} onRetry={catalogue.reload}>
              {target.explicit.message ? <span className="msgb-small" style={{ display: "block" }}>{t("messaging.builder.unavailable.waiting", "The linked message opens once the list loads.")}</span> : null}
            </FailureBanner>, "msgb-target-failed") : null
          ) : resolved.kind === "empty" ? (
            centred(<PageState variant="inline" empty emptyTitle={t("messaging.builder.empty.messages", "No message templates")}
              emptyBody={t("messaging.builder.empty.messagesText", "This workspace has no messages that the builder can edit yet.")} />)
          ) : (
            // pending (or switching language to the linked message): hold the target, open nothing else
            <>
              <div className="msgb-split" data-testid="msgb-target-pending">
                <EditorSkeleton lead={railTrigger} label={target.explicit.message ? t("messaging.builder.loading.linkedMessage", "Loading the linked message") : t("messaging.builder.loadingMessages", "Loading messages")} />
              </div>
              <FooterSkeleton />
            </>
          )
        ) : layoutResolved.kind === "selected" && layoutKey ? (
          <LayoutEditor t={t} store={layouts} layoutKey={layoutKey} library={lib} canWrite={canWrite} device={device} onDevice={setDevice}
            onOpenLayout={openLayout} onDeleted={() => { setLayoutTarget({ key: null, explicit: false }); library.reload(); }}
            onOpenTemplate={(templateId) => { if (leaveEditor()) openTemplate(templateId); }} railTrigger={railTrigger} />
        ) : layoutResolved.kind === "unavailable" ? (
          <Unavailable t={t} testid="msgb-unavailable-layout" railTrigger={railTrigger} title={t("messaging.builder.unavailable.layoutTitle", "The linked layout isn't available")}
            text={t("messaging.builder.unavailable.layoutText", "It isn't in this list: it may have been deleted. Nothing else has been opened in its place; choose a layout from the list.")}
            reference={layoutResolved.ref}
            actions={<>
              <Btn size="sm" variant="secondary" onClick={library.reload}>{t("messaging.builder.unavailable.reload", "Reload the list")}</Btn>
              <Btn size="sm" variant="ghost" onClick={() => revealList()}>{t("messaging.builder.unavailable.chooseLayout", "Choose a layout")}</Btn>
            </>} />
        ) : layoutResolved.kind === "failed" ? (
          library.failure ? centred(<FailureBanner t={t} failure={library.failure} onRetry={library.reload}>
            {layoutTarget.explicit ? <span className="msgb-small" style={{ display: "block" }}>{t("messaging.builder.unavailable.waitingLayout", "The linked layout opens once the list loads.")}</span> : null}
          </FailureBanner>, "msgb-target-failed") : null
        ) : layoutResolved.kind === "empty" ? (
          centred(<PageState variant="inline" empty emptyTitle={t("messaging.builder.empty.layouts", "No shared layouts")}
            emptyBody={t("messaging.builder.empty.layoutsText", "Create one to give emails a shared header, footer and styles.")}
            action={canWrite ? <Btn size="sm" variant="secondary" onClick={() => void newLayout("default")} disabled={creating || !defaultPublished}
              title={defaultPublished ? undefined : t("messaging.builder.newLayout.noDefault", "There is no published global default to copy.")}>
              {defaultRow ? `${t("messaging.builder.newLayout.duplicate", "Duplicate")} ${defaultRow.name}` : t("messaging.builder.newLayout.duplicateDefault", "Duplicate the global default")}</Btn> : undefined}
            secondaryAction={canWrite ? <Btn size="sm" variant="secondary" onClick={() => void newLayout("blank")} disabled={creating}>
              {t("messaging.builder.newLayout.blank", "Blank")}</Btn> : undefined} />)
        ) : (
          <>
            <div className="msgb-split" data-testid="msgb-target-pending">
              <EditorSkeleton lead={railTrigger} label={layoutTarget.explicit ? t("messaging.builder.loading.linkedLayout", "Loading the linked layout") : t("messaging.builder.loadingLayouts", "Loading layouts")} />
            </div>
            <FooterSkeleton />
          </>
        )}
      </main>
    </div>
  );
}
